import React, { useState } from 'react';
import {
  ShoppingCart, MapPin, Info, Nut, Leaf, Citrus,
  Coffee, Cake, Grape, Cherry, Star, Snowflake,
  Sparkles, ImagePlus, Shapes, Droplets, Layers,
  Trophy, Flame, Wine, Crown, Trees, Heart, Egg, Sun
} from 'lucide-react';
import ASZFModal from '../components/ASZFModal';
import CustomizeModal from '../components/CustomizeModal';
import '../styles/OrderPage.css';

const CAKE_ICONS = {
  1:  { icon: Nut,           color: '#8B6914' }, // Mogyorós     → dió/mogyoró ikon
  2:  { icon: Nut,           color: '#5C4A2A' }, // Diós         → dió ikon, sötétebb
  3:  { icon: Citrus,        color: '#D4A827' }, // Citrom       → citrus ikon
  4:  { icon: Snowflake,     color: '#C8A4C8' }, // Raffaello    → hópihe = kókusz fehér
  5:  { icon: Coffee,        color: '#3D1F0D' }, // Csoki        → kávé/csokoládé barna
  6:  { icon: Layers,        color: '#5C4033' }, // Kongó        → rétegek = komplex ízek
  7:  { icon: Trophy,        color: '#C9872A' }, // Dobos        → klasszikus, díjnyertes
  8:  { icon: Sparkles,      color: '#C9A84C' }, // Vanília      → csillogó, édes
  9:  { icon: Flame,         color: '#C9872A' }, // Karamell     → láng = karamellizált
  10: { icon: Leaf,          color: '#4A7C6B' }, // Japán        → levél = keleti
  11: { icon: Wine,          color: '#8B2252' }, // Puncs        → bor = puncs
  12: { icon: Crown,         color: '#B8975A' }, // Eszterházy   → korona = nemes
  13: { icon: Trees,         color: '#6B4226' }, // Gesztenyés   → erdő = gesztenye
  14: { icon: Coffee,        color: '#5C3D2E' }, // Tiramisu     → kávé ikon
  15: { icon: Heart,         color: '#D4516A' }, // Epres        → szív = piros/epres
  16: { icon: Egg,           color: '#C8A87A' }, // Oroszkrém    → tojás = krémes
  17: { icon: Trees,         color: '#4A3080' }, // Erdei gyüm.  → erdő ikon
  18: { icon: Sun,           color: '#D4A020' }, // Ananász      → nap = trópusi
  19: { icon: Cherry,        color: '#8B2230' }, // Feketeerdő   → cseresznye
  20: { icon: Droplets,      color: '#C8A87A' }, // Túrótorta    → krémes, folyékony
  21: { icon: Grape,         color: '#5A7D9B' }, // Zselés gyüm. → szőlő/gyümölcs
  formatorta_figura: { icon: Shapes,    color: '#D4816F' },
  formatorta_foto:   { icon: ImagePlus, color: '#D4816F' },
};

// Visszaad egy Lucide ikont a torta id-ja alapján
const CakeIcon = ({ cakeId, size = 28, strokeWidth = 1.5 }) => {
  const cfg = CAKE_ICONS[cakeId];
  if (!cfg) return <Cake size={size} strokeWidth={strokeWidth} color="#8B6F47" />;
  const Icon = cfg.icon;
  return <Icon size={size} strokeWidth={strokeWidth} color={cfg.color} />;
};

// ────────────────────────────────────────────────────────────────────────────

const OrderPage = ({
  cakeTypes,
  cartItems,
  orderForm,
  setOrderForm,
  openCustomizeModal,
  isModalOpen,
  setIsModalOpen,
  selectedCake,
  addToCart,
  globalSizes,
  globalOstya,
  updateQuantity,
  handleSubmitOrder,
  totalPrice,
}) => {
  const [showASZFModal, setShowASZFModal] = useState(false);

  const standardCakes = cakeTypes.filter(cake => !cake.isSpecial);
  const specialCakes  = cakeTypes.filter(cake => cake.isSpecial);

  return (
    <div className="order-page">
      <div className="order-header">
        <h1>Torta Előrendelés</h1>
        <p>Válassza ki kedvenc süteményét, majd szabja személyre</p>
      </div>

      <div className="order-layout">

        {/* ── BAL OLDAL: KATALÓGUS ── */}
        <div className="cake-catalog">

          {/* Hagyományos torták */}
          <h2 className="section-title">Hagyományos Torták</h2>
          <div className="catalog-grid">
            {standardCakes.map(cake => (
              <div key={cake.id} className="cake-card">
                <div className="cake-icon-wrap">
                  <CakeIcon cakeId={cake.id} size={30} />
                </div>
                <h3>{cake.name}</h3>
                <p className="cake-description">{cake.description}</p>
                <div className="cake-details">
                  <span className="base-price">7 200 Ft-tól</span>
                </div>
                <button
                  className="add-to-cart-btn"
                  onClick={() => openCustomizeModal(cake)}
                >
                  <ShoppingCart size={16} strokeWidth={1.5} />
                  Testreszabás
                </button>
              </div>
            ))}
          </div>

          {/* Formatorták */}
          {specialCakes.length > 0 && (
            <div className="special-section-container">
              <h2 className="section-title special-title">Különleges Formatorták</h2>

              <div className="special-info-banner">
                <Info size={18} strokeWidth={1.5} className="special-info-icon" />
                <p>
                  Ezek fix áras termékek marcipán bevonattal. A „Összeállítás" gombra
                  kattintva írhatja le, milyen formát szeretne (pl. focilabda, cica).{' '}
                  <strong>Ha egyeztetés szükséges, visszahívjuk Önt!</strong>
                </p>
              </div>

              <div className="catalog-grid">
                {specialCakes.map(cake => (
                  <div key={cake.id} className="cake-card special-card">
                    <div className="cake-icon-wrap cake-icon-wrap--special">
                      <CakeIcon cakeId={cake.id} size={30} />
                    </div>
                    <h3>{cake.name}</h3>
                    <p className="cake-description">{cake.description}</p>
                    <div className="cake-details">
                      <span className="base-price base-price--special">
                        {cake.customSizes?.[0]
                          ? `${cake.customSizes[0].price.toLocaleString()} Ft-tól`
                          : 'Egyedi ár'}
                      </span>
                    </div>
                    <button
                      className="add-to-cart-btn add-to-cart-btn--special"
                      onClick={() => openCustomizeModal(cake)}
                    >
                      <ShoppingCart size={16} strokeWidth={1.5} />
                      Összeállítás
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── JOBB OLDAL: KOSÁR & ŰRLAP ── */}
        <div className="order-sidebar">
          <div className="cart-section" id="cart-section">
            <h2>Kosár</h2>
            {cartItems.length === 0 ? (
              <p className="empty-cart">A kosár üres</p>
            ) : (
              <>
                {cartItems.map(item => (
                  <div key={item.id} className="cart-item">
                    <div className="cart-item-info">
                      {/* Kosárban is ikon az emoji helyett */}
                      <div className="cart-icon-wrap">
                        <CakeIcon cakeId={item.id} size={20} />
                      </div>
                      <div>
                        <h4>{item.name}</h4>
                        <p className="cart-details">{item.size} • {item.ostya}</p>
                        <p className="cart-price">{item.price.toLocaleString('hu-HU')} Ft</p>
                      </div>
                    </div>
                    <div className="quantity-controls">
                      <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>−</button>
                      <span>{item.quantity}</span>
                      <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                    </div>
                  </div>
                ))}
                <div className="cart-total">
                  <span>Összesen:</span>
                  <span className="total-price">{totalPrice.toLocaleString('hu-HU')} Ft</span>
                </div>
              </>
            )}
          </div>

          {cartItems.length > 0 && (
            <form className="order-form" onSubmit={handleSubmitOrder}>
              <h3>Rendelési Adatok</h3>

              <div className="form-group">
                <label>Név *</label>
                <input type="text" required value={orderForm.name}
                  onChange={e => setOrderForm({...orderForm, name: e.target.value})}
                  placeholder="Kovács János" />
              </div>

              <div className="form-group">
                <label>Telefon *</label>
                <input type="tel" required value={orderForm.phone}
                  onChange={e => setOrderForm({...orderForm, phone: e.target.value})}
                  placeholder="+36 30 123 4567" />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input type="email" required value={orderForm.email}
                  onChange={e => setOrderForm({...orderForm, email: e.target.value})}
                  placeholder="pelda@email.hu" />
              </div>

              <div className="form-group">
                <label>Átvétel Dátuma és Időpontja *</label>
                <input
                  type="datetime-local"
                  required
                  value={orderForm.pickupDate}
                  onChange={e => {
                    const val = e.target.value;
                    if (!val) { setOrderForm({...orderForm, pickupDate: ''}); return; }
                    const hours = new Date(val).getHours();
                    if (hours < 8 || hours >= 18) {
                      alert('Kérjük, nyitvatartási időben (08:00–18:00) válasszon időpontot!');
                      setOrderForm({...orderForm, pickupDate: val.split('T')[0] + 'T08:00'});
                    } else {
                      setOrderForm({...orderForm, pickupDate: val});
                    }
                  }}
                  min={(() => {
                    const d = new Date();
                    d.setDate(d.getDate() + 3);
                    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T08:00`;
                  })()}
                />
                <div className="pickup-note">
                  <Info size={15} strokeWidth={1.5} />
                  <span>Legalább <strong>3 nappal</strong> előre kell rendelni · Nyitva: <strong>08:00–18:00</strong></span>
                </div>
                <div className="pickup-address">
                  <MapPin size={15} strokeWidth={1.5} />
                  <span><strong>Átvétel:</strong> 6724 Szeged, Kossuth Lajos sgt. 45.</span>
                </div>
              </div>

              <div className="form-group">
                <label>Megjegyzés</label>
                <textarea
                  value={orderForm.notes}
                  onChange={e => setOrderForm({...orderForm, notes: e.target.value})}
                  placeholder="Különleges kérések, allergének, stb."
                  rows="3"
                />
              </div>

              <div className="form-group aszf-group">
                <label className="aszf-checkbox">
                  <input type="checkbox" required className="aszf-input" />
                  <span className="aszf-text">
                    Elfogadom az{' '}
                    <a href="#" onClick={e => { e.preventDefault(); setShowASZFModal(true); }}>
                      Általános Szerződési Feltételeket
                    </a>
                    , beleértve hogy adataimat marketing célokra is felhasználhatják. *
                  </span>
                </label>
              </div>

              <button type="submit" className="submit-order-btn">
                Rendelés Leadása
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Mobil lebegő kosár */}
      {cartItems.length > 0 && (
        <div className="mobile-cart-float"
          onClick={() => document.querySelector('.cart-section')?.scrollIntoView({ behavior: 'smooth' })}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ShoppingCart size={20} strokeWidth={1.5} />
            <span className="float-badge">{cartItems.reduce((s, i) => s + i.quantity, 0)} db</span>
          </div>
          <span>Ugrás a pénztárhoz ({totalPrice.toLocaleString('hu-HU')} Ft)</span>
        </div>
      )}

      <ASZFModal isOpen={showASZFModal} onClose={() => setShowASZFModal(false)} />

      {selectedCake && (
        <CustomizeModal
          selectedCake={selectedCake}
          customization={orderForm}
          setCustomization={setOrderForm}
          sizes={globalSizes}
          ostyaOptions={globalOstya}
          calculatePrice={(cake, sizeId) => {
            const s = globalSizes.find(x => x.id === sizeId);
            return s ? s.price : 0;
          }}
          addToCart={addToCart}
          closeCustomizeModal={() => setIsModalOpen(false)}
          allCakeTypes={cakeTypes}
        />
      )}
    </div>
  );
};

export default OrderPage;